import { IMSTokenParams, getWithIDToken, postWithIdToken } from "@utils/http/MSAuthAPI";


/**
 * Matches the EtlStatus payload from splend_api/chargebee_pricing_etl.py.
 * All timestamps are ISO-8601 strings; null means "never happened".
 */
export interface IExportStatus {
    /** Entity name ("plans" | "addons" | "coupons") -> S3 LastModified of that CSV. */
    files: { [entity: string]: string | null };
    inFlight: boolean;
}

export interface ILoadStatus {
    pricingLastChanged: string | null;
    running: boolean;
    recentFailure: boolean;
}

export interface IBlockedReasons {
    export: string | null;
    load: string | null;
}

export interface IChargebeePricingEtlStatus {
    country: string;
    export: IExportStatus;
    load: ILoadStatus;
    blocked: IBlockedReasons;
}


interface IChargebeePricingEtlService {
    getStatus(country: string, IDTokenParams: IMSTokenParams): Promise<IChargebeePricingEtlStatus>;
    triggerExport(country: string, IDTokenParams: IMSTokenParams): Promise<string>;
    triggerLoad(country: string, IDTokenParams: IMSTokenParams): Promise<string>;
}

/**
 * Both triggers answer 409 with a human-readable reason when a conflicting run is already in
 * progress, and Axios rejects on any 4xx. Re-throw carrying that reason so the view can show it
 * verbatim rather than a generic failure.
 */
const rethrowWithBackendMessage = (error: any, fallback: string): never => {
    throw new Error(error?.response?.data?.message ?? fallback);
};

export const ChargebeePricingEtlService: IChargebeePricingEtlService = {
    async getStatus(country: string, IDTokenParams: IMSTokenParams): Promise<IChargebeePricingEtlStatus> {
        const resp = await getWithIDToken(
            `/v1/chargebee-pricing-etl/${country}/status`,
            IDTokenParams,
        );
        if (!resp.data?.export || !resp.data?.load) {
            throw new Error('Unexpected response format from Chargebee pricing ETL status endpoint');
        }
        return resp.data;
    },

    async triggerExport(country: string, IDTokenParams: IMSTokenParams): Promise<string> {
        try {
            const resp = await postWithIdToken(
                `/v1/chargebee-pricing-etl/${country}/export`,
                IDTokenParams,
            );
            return resp.data?.message ?? 'Chargebee export triggered';
        } catch (error) {
            return rethrowWithBackendMessage(error, 'Could not trigger the Chargebee export');
        }
    },

    async triggerLoad(country: string, IDTokenParams: IMSTokenParams): Promise<string> {
        try {
            const resp = await postWithIdToken(
                `/v1/chargebee-pricing-etl/${country}/load`,
                IDTokenParams,
            );
            return resp.data?.message ?? 'Chargebee pricing load triggered';
        } catch (error) {
            return rethrowWithBackendMessage(error, 'Could not trigger the pricing load');
        }
    },
};
